// Belegübersicht: zuerst die offenen Aufgaben, dann der Weg eines Belegs (eingegangen -> geprueft -> Bankumsatz zugeordnet -> Kontierung -> DATEV), Schnellaktionen
// und der technische Bereitschaftsstand. Inhalte und Aufrufe entsprechen der bisherigen Belege-Uebersicht; es wurde nur die Reihenfolge/Darstellung geaendert.
// Technische Details (Pruefsummen, interne IDs) bleiben in der Detailansicht des einzelnen Belegs.
import { useState, useEffect } from "react";
import THEME from "../../../../theme";
import { fs } from "../../utils/styles";
import { belegeApi, STATUS_NAME, belegeStufe } from "../../../belege/belegeApi";
import { Klein, Meldung, useAktion } from "../../../belege/ui";
import { Seitenkopf, Abschnitt, Pille, useBreite, BREAKPOINT_KLEIN } from "../../ui/Bausteine";

const SCHRITTE = [
  ["eingegangen", "Eingegangen", "Beleg liegt vor, noch nicht geprüft"],
  ["geprueft", "Geprüft / erkannt", "Angaben der Rechnung geprüft"],
  ["bankumsatz_zugeordnet", "Bankumsatz zugeordnet", "Zahlung gefunden und bestätigt"],
  ["an_datev_gesendet", "An DATEV übergeben", "Sendung ausgelöst"],
  ["in_datev_bestaetigt", "In DATEV bestätigt", "Nachweis liegt vor"],
];
const BEREICH_ZIEL = {
  umsaetze_ohne_beleg: ["bank", "Zum Bankabgleich"], unvollstaendig: ["belege", "Belege öffnen", { status: "eingegangen" }], ruecklauf: ["belege", "Rückfragen öffnen", { status: "rueckfrage" }],
  ohne_bankzuordnung: ["bank", "Zur Bankzuordnung"], ohne_kontierung: ["kontierung", "Zur Kontierung"], nicht_an_datev: ["datev", "Zur DATEV-Vorbereitung"],
  ohne_bestaetigung: ["belege", "Belege öffnen", { status: "an_datev_gesendet" }], amazon_rechnung_fehlt: ["amazon", "Zum Amazon-Import"],
};

export default function BelegeUebersicht({ geheZu }) {
  const erweitert = belegeStufe.erweitert;   // Stufe basis: ohne Postfach, Erkennung, DATEV; Weg eines Belegs endet bei der Bankzuordnung
  const schritte = erweitert ? SCHRITTE : SCHRITTE.slice(0, 3);
  const [fehlend, setFehlend] = useState(null);
  const [liste, setListe] = useState(null);
  const [bereit, setBereit] = useState(null);
  const [fehler, setFehler] = useState("");
  const [hinweis, setHinweis] = useState("");
  const [technik, setTechnik] = useState(false);
  const aktion = useAktion(setFehler, setHinweis);
  const breite = useBreite();
  useEffect(() => {
    belegeApi.uebersichtFehlend().then(setFehlend).catch(e => setFehler(e.message));
    belegeApi.liste({ limit: 1 }).then(setListe).catch(e => setFehler(e.message));
    if (erweitert) Promise.all([belegeApi.ocrStatus(), belegeApi.postfachStatus(), belegeApi.datevStatus(), belegeApi.extfListe(), belegeApi.einstellungen()])
      .then(([ocr, post, datev, extf, einst]) => setBereit({ ocr, post, datev, extf, einst })).catch(() => {});
  }, []);
  const z = liste?.status_zaehler || {};
  const offene = Object.entries(fehlend?.bereiche || {}).filter(([, b]) => b.anzahl > 0);
  const kontierungOffen = fehlend?.bereiche?.ohne_kontierung?.anzahl || 0;

  return (
    <div style={{ color: THEME.colors.text }}>
      <Seitenkopf titel="Belegübersicht" untertitel="Was ist zu tun – und wo steht jeder Beleg?" />
      <Meldung fehler={fehler} hinweis={hinweis} />

      <Abschnitt titel="Offene Aufgaben" rechts={<span style={{ color: THEME.colors.textMuted }}>{fehlend ? `${fehlend.gesamt} offene Punkte` : "Lädt…"}</span>}>
        {fehlend && offene.length === 0 && <div style={{ color: THEME.colors.success }}>Alles erledigt – nichts Offenes.</div>}
        <div style={{ display: "grid", gap: 8 }}>
          {offene.map(([k, b]) => {
            const [ziel, text, opt] = BEREICH_ZIEL[k] || ["fehlend", "Ansehen"];
            return (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", border: `1px solid ${THEME.colors.border}`, borderLeft: `4px solid ${THEME.colors.warning}`, borderRadius: THEME.radius.md, padding: "10px 12px" }}>
                <div style={{ minWidth: 200, flex: 1 }}><strong style={{ color: THEME.colors.warning }}>{b.anzahl}</strong> · {b.titel}<Klein>{b.hinweis}</Klein></div>
                <button style={fs.btn} onClick={() => geheZu(ziel, opt)}>{text}</button>
              </div>
            );
          })}
        </div>
      </Abschnitt>

      <Abschnitt titel="Weg eines Belegs">
        <div style={{ display: "grid", gridTemplateColumns: breite < BREAKPOINT_KLEIN ? "1fr" : "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          {schritte.map(([s, name, hilfe], i) => (
            <button key={s} onClick={() => geheZu("belege", { status: s })} style={{ textAlign: "left", cursor: "pointer", background: THEME.colors.surfaceMuted, border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.lg, padding: "12px 14px", color: THEME.colors.text, position: "relative" }}>
              <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{i + 1}. {name}</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{z[s] || 0}</div>
              <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.xs }}>{hilfe}</div>
              {erweitert && s === "bankumsatz_zugeordnet" && kontierungOffen > 0 && <div style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, marginTop: 4 }}>davon Kontierung offen: {kontierungOffen}</div>}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {["rueckfrage", "dublette", "abgelehnt"].map(s => <Pille key={s} text={STATUS_NAME[s]} zahl={z[s] || 0} onClick={() => geheZu("belege", { status: s })} farbe={s === "rueckfrage" ? THEME.colors.warning : undefined} />)}
        </div>
      </Abschnitt>

      <Abschnitt titel="Schnellaktionen">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {erweitert && <button style={fs.btnGray} onClick={() => aktion(() => belegeApi.erkennungOffene(), r => `Erkennung: ${r.ok} von ${r.geprueft} Belegen gelesen${r.fehler ? `, ${r.fehler} Fehler` : ""}.`)}>🔍 Neue Belege automatisch lesen</button>}
          <button style={fs.btnGray} onClick={() => aktion(() => belegeApi.abgleichLauf(), r => `Bankabgleich: ${r.mit_vorschlaegen} von ${r.belege} Belegen mit Vorschlägen, ${r.sichere_treffer} sichere Treffer.`)}>🏦 Bankabgleich für alle offenen Belege</button>
          <button style={fs.btnGray} onClick={() => aktion(() => belegeApi.kontierungStapel(), r => `Kontierung: ${r.mit_vorschlag} Vorschläge berechnet, ${r.ohne_vorschlag} ohne Vorschlag.`)}>📒 Buchungsvorschläge für alle berechnen</button>
          {erweitert && <button style={fs.btnGray} onClick={() => geheZu("posteingang")}>✉ Rechnungspostfach abrufen…</button>}
        </div>
        <Klein>Alles hier berechnet nur Vorschläge. Bestätigt, gesendet oder exportiert wird erst auf deinen ausdrücklichen Klick.</Klein>
      </Abschnitt>

      {erweitert && bereit && (
        <Abschnitt titel="Technischer Stand (vor der Live-Schaltung prüfen)" rechts={<button style={fs.btnGray} onClick={() => setTechnik(!technik)}>{technik ? "Ausblenden" : "Anzeigen"}</button>}>
          {!technik && <Klein>{bereitKurz(bereit)}</Klein>}
          {technik && <Bereitschaft b={bereit} geheZu={geheZu} />}
        </Abschnitt>
      )}
    </div>
  );
}

function zeilenVon(b) {
  return [
    ["Lokale Texterkennung (OCR)", b.ocr.engine ? `bereit (${b.ocr.engine})` : "nicht installiert – nur PDFs mit Text werden gelesen", !!b.ocr.engine],
    ["Rechnungspostfach (IMAP)", b.post.konfiguration.modus === "aus" ? "ausgeschaltet" : `Modus ${b.post.konfiguration.modus}${b.post.konfiguration.host && b.post.konfiguration.passwort ? "" : " – Zugangsdaten fehlen"}`, b.post.konfiguration.modus !== "aus" && b.post.konfiguration.host && b.post.konfiguration.passwort, "posteingang"],
    ["DATEV-Upload-Mail", b.datev.konfiguration.modus === "aus" || b.datev.konfiguration.modus === "vorschau" ? `Modus ${b.datev.konfiguration.modus} – Versand gesperrt` : `Modus ${b.datev.konfiguration.modus}`, ["test", "live"].includes(b.datev.konfiguration.modus) && b.datev.konfiguration.smtp, "datev"],
    ["EXTF-Export (Berater-/Mandantennr.)", b.extf.einstellungen_vollstaendig ? "vollständig" : "Beraternummer/Mandantennummer fehlen", b.extf.einstellungen_vollstaendig, "kontierung"],
  ];
}
const bereitKurz = (b) => { const z = zeilenVon(b); return `${z.filter(x => x[2]).length} von ${z.length} Voraussetzungen erfüllt – Details anzeigen.`; };

function Bereitschaft({ b, geheZu }) {
  const ja = THEME.colors.success, nein = THEME.colors.warning;
  return (
    <div>
      <div style={{ display: "grid", gap: 6 }}>
        {zeilenVon(b).map(([name, text, ok, ziel]) => (
          <div key={name} style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <span>{ok ? "✅" : "⚠️"} {name}</span>
            <span style={{ color: ok ? ja : nein }}>{text}{ziel && !ok ? <button style={{ ...fs.btnGray, padding: "2px 10px", marginLeft: 8 }} onClick={() => geheZu(ziel)}>öffnen</button> : null}</span>
          </div>
        ))}
      </div>
      <Klein>Schalter wie Modus, Zugangsdaten und Versandadressen stehen ausschließlich auf dem Server (nicht in dieser Oberfläche, nicht im Quelltext).</Klein>
    </div>
  );
}
